/**
 * Carregamento dos dados das embarcações e revalidação periódica.
 *
 * Os arquivos só são baixados quando a camada é ligada pela primeira vez —
 * quem nunca abre a aba Barcos não paga nada. Depois disso, o gancho de
 * atualização automática do loop revalida a cada ciclo, porque o pipeline
 * publica junto com a chuva, de 15 em 15 minutos.
 */
import { carregarDados } from './api';
import { frescorDe } from './frescor';
import { useShipStore } from './store';

let emVoo: AbortController | null = null;
let jaCarregou = false;

/** Baixa posições + cadastro + escuta. `force` revalida mesmo já tendo dados. */
export async function carregarNavios(force = false): Promise<void> {
  const { situacao } = useShipStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;

  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;

  // A revalidação periódica é silenciosa: "Baixando…" piscando no painel a
  // cada ciclo automático, para um dado que quase sempre não mudou, é ruído.
  if (!force) useShipStore.getState().setSituacao({ estado: 'loading', mensagem: 'Baixando as embarcações…', erro: null });

  try {
    const dados = await carregarDados(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Publicação igual à que já está na tela: não trocar `dados` poupa a
    // reconstrução das features e todos os `useMemo` que dependem dela.
    const atual = useShipStore.getState().dados;
    if (dados.gerado !== null && dados.gerado === atual.gerado) {
      useShipStore.getState().setSituacao({ estado: 'ready', mensagem: null, erro: null });
      return;
    }
    useShipStore.getState().setDados(dados);
    useShipStore.getState().setSituacao({
      estado: 'ready',
      mensagem: dados.escuta.length === 0 ? 'Área de recepção indisponível nesta publicação.' : null,
      erro: null,
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useShipStore.getState().setSituacao({
      estado: 'error',
      mensagem: null,
      erro: e instanceof Error ? e.message : String(e),
    });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só se a camada estiver em uso. */
export function refreshNavios(): void {
  if (!jaCarregou || !useShipStore.getState().visivel) return;
  void carregarNavios(true);
}

/**
 * Liga o carregamento sob demanda: a primeira vez que a camada é ligada,
 * baixa. Devolve o cancelamento.
 */
export function startShipSync(): () => void {
  if (useShipStore.getState().visivel) void carregarNavios();
  return useShipStore.subscribe((a, b) => {
    if (a.visivel && !b.visivel) void carregarNavios();
  });
}

/**
 * Ponto amarelo no trilho quando o dado publicado está velho. É o mesmo
 * mecanismo dos focos e das estações.
 */
export function useNaviosDesatualizados(): boolean {
  const gerado = useShipStore((s) => s.dados.gerado);
  const visivel = useShipStore((s) => s.visivel);
  const erro = useShipStore((s) => s.situacao.estado === 'error');
  if (!visivel) return false;
  const f = frescorDe(gerado);
  return erro || f === 'atrasado' || f === 'velho';
}

/** Só para os testes. */
export function resetShipSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
}
